import { TranslateIcon } from '@/components/icons';
import { Dropdown } from '@/components/ui';

// Each label is the language's own endonym so the menu reads natively in
// every option's locale, not just the current one.
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'ko', label: '한국어' },
  { value: 'es', label: 'Español' },
  { value: 'de', label: 'Deutsch' },
] as const;

interface LanguageDropdownProps {
  /** Selected language code (e.g. `'en'`). */
  value: string;
  onChange: (value: string) => void;
  /**
   * When true, removes the trigger's border + hover/open background. Used by
   * the desktop nav-header instance so the picker reads as plain text + icons.
   * The footer / mobile-menu instances leave this off so they get the
   * outlined chrome.
   */
  borderless?: boolean;
  /**
   * Trigger size — passed straight through to {@link Dropdown}. Defaults to
   * `'sm'` (14px label + tighter chrome) since both bordered placements
   * (footer + mobile menu) and the borderless desktop nav use the compact
   * shape; pass `'lg'` if a future spot needs the form-field-sized variant.
   */
  size?: 'lg' | 'sm';
  /** Wrapper-level classes — controls width (also sizes the menu). */
  className?: string;
  /** Trigger-level classes — e.g. `px-0 py-1.5` to override default padding. */
  triggerClassName?: string;
  /** Passthrough — see {@link Dropdown}. */
  menuPlacement?: 'bottom' | 'top';
  /** Passthrough — see {@link Dropdown}. */
  menuPortalTarget?: HTMLElement | null;
  /**
   * Passthrough for the menu's outer (positioning) wrapper. When omitted,
   * the borderless variant falls back to its default nav-aligned offsets;
   * pass an explicit value to override (e.g. when portaling).
   */
  menuWrapperClassName?: string;
  /**
   * Passthrough for the menu's inner (chrome) box. When omitted, the
   * borderless variant falls back to its frosted nav-pill chrome; pass
   * an explicit value to override (e.g. transparent inside a portal
   * target that already supplies the chrome).
   */
  menuClassName?: string;
}

/**
 * Language picker — thin wrapper around {@link Dropdown} that bakes in the
 * shared language options + leading translate icon. Two visual flavours:
 *
 *   - Outlined (default): bordered pill matching the footer's design. Used
 *     by the footer language card and the mobile nav menu.
 *   - Borderless: text + icon only. Used by the desktop nav-header pill,
 *     where a fixed `w-[104px]` keeps the chevron right-aligned and the
 *     icon + label left-aligned via `justify-between`.
 *
 * The same `Dropdown` machinery (option list, outside-click close, etc.)
 * powers both — only the trigger chrome differs.
 */
export function LanguageDropdown({
  value,
  onChange,
  borderless = false,
  size = 'sm',
  className,
  triggerClassName,
  menuPlacement,
  menuPortalTarget,
  menuWrapperClassName,
  menuClassName,
}: LanguageDropdownProps) {
  // Borderless (desktop nav header) defaults — applied when the consumer
  // doesn't pass an explicit override:
  //   - `left-auto -right-4`: cancel the default `left-0`, then push the
  //     menu's right edge 16px past the trigger's right edge. The nav
  //     pill has uniform 16px padding (Figma 140:1940) and the trigger
  //     sits flush at `justify-between`'s right end, so trigger.right =
  //     pill.content.right = pill.outer.right − 16. `-right-4` lands the
  //     menu's right edge exactly on the pill's outer right edge.
  //   - `w-max`: force `width: max-content` so the menu hugs the
  //     longest option label (intrinsic sizing on an absolutely-
  //     positioned grid isn't reliable across engines).
  //   - `mt-6`: trigger.bottom + 24px = pill.bottom + 8px (16px pill
  //     bottom-padding + 8px breathing room) so the menu drops 8px
  //     below the nav box rather than below the trigger itself.
  // Plus a chrome reskin on the inner so the menu reads as a continuation
  // of the nav pill (frosted bg + pill shadow + backdrop blur, no border).
  const resolvedMenuWrapperClassName =
    menuWrapperClassName ?? (borderless ? 'left-auto -right-4 mt-6 w-max' : undefined);
  const resolvedMenuClassName =
    menuClassName ??
    (borderless ? 'border-0 bg-[#edeceb]/70 shadow-pill backdrop-blur-pill' : undefined);

  return (
    <Dropdown
      value={value}
      onChange={onChange}
      options={LANGUAGE_OPTIONS}
      placeholder="English"
      leadingIcon={<TranslateIcon className="size-4" />}
      size={size}
      borderless={borderless}
      className={className}
      triggerClassName={triggerClassName}
      menuPlacement={menuPlacement}
      menuPortalTarget={menuPortalTarget}
      menuWrapperClassName={resolvedMenuWrapperClassName}
      menuClassName={resolvedMenuClassName}
      aria-label="Change language"
    />
  );
}
